import Indicator from '../../../app/builder/condition/indicators/types/Indicator'
import {
  COMPOSITE_LIMITS,
  CompositeExpr,
  CompositeNode,
  CompositeOutput,
  CompositeRef,
  OUTPUT_FIELD_REGEX,
  RESERVED_OUTPUT_FIELDS,
  SOURCE_FIELD_VALUES,
} from '../../../types/composite'
import { computeMinCandles } from './computeWarmup'
import { NODE_CATALOG, nodeOutputs } from './nodeCatalog'
import { exprDepth, isNodeRef, nodeRefs } from './refs'

export type CompositeValidationIssue = {
  level: 'error' | 'warning'
  nodeId?: string
  outputIndex?: number
  /** Still blocks publish, but the builder shows only the red glow — the
   * state is self-explanatory at the element (e.g. an empty indicator
   * select), so message text would be noise. */
  silent?: boolean
  message: string
}

/** Where an issue should surface in the builder: on a step card, on an output
 * row, or (no anchor) in the editor-level list. */
export type CompositeIssueAnchor = Pick<CompositeValidationIssue, 'nodeId' | 'outputIndex'>

export type CompositeValidationResult = {
  valid: boolean
  issues: CompositeValidationIssue[]
  minCandles: number
}

type IndicatorLookup = (value: string) => Indicator | undefined

/** Output names a ref against this node may use. Source and math nodes expose one implicit output. */
const refTargetOutputs = (node: CompositeNode, getIndicatorByValue: IndicatorLookup): string[] => {
  if (node.kind === 'indicator') return nodeOutputs(node.name, getIndicatorByValue)
  return ['value']
}

const validateRef = (
  ref: CompositeRef,
  earlier: Map<string, CompositeNode>,
  getIndicatorByValue: IndicatorLookup,
  where: string,
  anchor: CompositeIssueAnchor,
  issues: CompositeValidationIssue[]
) => {
  const offset = ref.offset ?? 0
  if (!Number.isInteger(offset) || offset < 0 || offset > COMPOSITE_LIMITS.maxOffset) {
    issues.push({
      level: 'error',
      ...anchor,
      message: `${where} offset must be a whole number between 0 and ${COMPOSITE_LIMITS.maxOffset}`,
    })
  }
  if (!isNodeRef(ref)) {
    if (!SOURCE_FIELD_VALUES.includes(ref.source)) {
      issues.push({
        level: 'error',
        ...anchor,
        message: `${where} references unknown chart field "${ref.source}"`,
      })
    }
    return
  }
  const target = earlier.get(ref.node)
  if (!target) {
    issues.push({ level: 'error', ...anchor, message: `${where} must reference an earlier step` })
    return
  }
  const outputs = refTargetOutputs(target, getIndicatorByValue)
  if (outputs.length > 1) {
    if (!ref.output) {
      issues.push({
        level: 'error',
        ...anchor,
        message: `${where} must pick one of the referenced step's outputs (${outputs.join(', ')})`,
      })
    } else if (!outputs.includes(ref.output)) {
      issues.push({
        level: 'error',
        ...anchor,
        message: `${where} references unknown output "${ref.output}"`,
      })
    }
  } else if (ref.output && ref.output !== outputs[0]) {
    issues.push({
      level: 'error',
      ...anchor,
      message: `${where} references unknown output "${ref.output}"`,
    })
  }
}

const validateExpr = (expr: CompositeExpr, nodeId: string, issues: CompositeValidationIssue[]) => {
  if (expr.t === 'const') {
    if (typeof expr.value !== 'number' || !Number.isFinite(expr.value)) {
      issues.push({ level: 'error', nodeId, message: 'Numbers in math steps must be finite' })
    }
    return
  }
  if (expr.t === 'ref') return
  const unary = expr.op === 'abs' || expr.op === 'neg' || expr.op === 'not'
  // Chained comparisons (a > b > c) are ambiguous — comparisons are strictly binary
  const binaryOnly = expr.op === '>' || expr.op === '<' || expr.op === '>=' || expr.op === '<='
  if (unary && expr.args.length !== 1) {
    issues.push({ level: 'error', nodeId, message: `"${expr.op}" takes exactly one operand` })
  }
  if (binaryOnly && expr.args.length !== 2) {
    issues.push({ level: 'error', nodeId, message: `"${expr.op}" takes exactly two operands` })
  }
  if (!unary && !binaryOnly && expr.args.length < 2) {
    issues.push({ level: 'error', nodeId, message: `"${expr.op}" needs at least two operands` })
  }
  expr.args.forEach((arg) => validateExpr(arg, nodeId, issues))
}

export const validateComposite = (
  nodes: CompositeNode[],
  outputs: CompositeOutput[],
  getIndicatorByValue: IndicatorLookup
): CompositeValidationResult => {
  const issues: CompositeValidationIssue[] = []

  if (nodes.length === 0) {
    issues.push({ level: 'error', message: 'Add at least one step' })
  }
  if (nodes.length > COMPOSITE_LIMITS.maxNodes) {
    issues.push({ level: 'error', message: `At most ${COMPOSITE_LIMITS.maxNodes} steps allowed` })
  }

  const earlier = new Map<string, CompositeNode>()
  const allNodes = new Map<string, CompositeNode>()
  for (const node of nodes) {
    if (allNodes.has(node.id)) {
      issues.push({ level: 'error', nodeId: node.id, message: 'Duplicate step id' })
    }

    if (node.kind === 'indicator') {
      const catalog = NODE_CATALOG[node.name]
      if (!catalog) {
        // Covers the just-added step with nothing selected yet — the glowing
        // card with an empty indicator select says it all
        issues.push({
          level: 'error',
          nodeId: node.id,
          silent: true,
          message: `"${node.name}" is not available in custom indicators`,
        })
      } else if (node.input && !catalog.acceptsSeriesInput) {
        issues.push({
          level: 'error',
          nodeId: node.id,
          message: `${getIndicatorByValue(node.name)?.label ?? node.name} computes from chart candles and cannot take a step input`,
        })
      }
      if (catalog && !getIndicatorByValue(node.name)) {
        issues.push({
          level: 'error',
          nodeId: node.id,
          message: `"${node.name}" is missing from the indicator registry`,
        })
      }
      // A cleared numeric param arrives as NaN (parseFloat('')); it must not
      // reach a published, immutable def — and NaN JSON-serializes to null,
      // so library and pattern copies of the def would silently diverge.
      Object.entries(node.params ?? {}).forEach(([key, value]) => {
        if (typeof value === 'number' && !Number.isFinite(value)) {
          issues.push({
            level: 'error',
            nodeId: node.id,
            message: `Parameter "${key}" is empty or invalid`,
          })
        }
      })
    }

    if (node.kind === 'math') {
      if (exprDepth(node.expr) > COMPOSITE_LIMITS.maxExprDepth) {
        issues.push({
          level: 'error',
          nodeId: node.id,
          message: `Math steps can nest at most ${COMPOSITE_LIMITS.maxExprDepth} levels`,
        })
      }
      validateExpr(node.expr, node.id, issues)
    }

    nodeRefs(node).forEach((ref) =>
      validateRef(ref, earlier, getIndicatorByValue, 'Step input', { nodeId: node.id }, issues)
    )

    earlier.set(node.id, node)
    allNodes.set(node.id, node)
  }

  if (outputs.length === 0) {
    issues.push({ level: 'error', message: 'Expose at least one output' })
  }
  if (outputs.length > COMPOSITE_LIMITS.maxOutputs) {
    issues.push({
      level: 'error',
      message: `At most ${COMPOSITE_LIMITS.maxOutputs} outputs allowed`,
    })
  }
  const seenFields = new Set<string>()
  outputs.forEach((output, outputIndex) => {
    if (!OUTPUT_FIELD_REGEX.test(output.field)) {
      issues.push({
        level: 'error',
        outputIndex,
        message: `Output name "${output.field}" must start with a letter and use only letters, numbers, and underscores`,
      })
    } else if (RESERVED_OUTPUT_FIELDS.has(output.field)) {
      issues.push({
        level: 'error',
        outputIndex,
        message: `Output name "${output.field}" is reserved`,
      })
    } else if (output.field.endsWith('Angle')) {
      issues.push({
        level: 'error',
        outputIndex,
        message: `Output name "${output.field}" cannot end with "Angle" (reserved for slope outputs)`,
      })
    }
    if (seenFields.has(output.field)) {
      issues.push({
        level: 'error',
        outputIndex,
        message: `Duplicate output name "${output.field}"`,
      })
    }
    seenFields.add(output.field)
    if (!isNodeRef(output.ref as CompositeRef)) {
      // Type-level already; runtime guard for agent-supplied drafts
      issues.push({
        level: 'error',
        outputIndex,
        message: `Output "${output.field}" must expose a step`,
      })
    } else {
      validateRef(
        output.ref,
        allNodes,
        getIndicatorByValue,
        `Output "${output.field}"`,
        { outputIndex },
        issues
      )
    }
  })

  const minCandles = computeMinCandles(nodes, outputs)
  if (minCandles > COMPOSITE_LIMITS.maxMinCandles) {
    issues.push({
      level: 'error',
      message: `This indicator needs ${minCandles} candles of history — the maximum is ${COMPOSITE_LIMITS.maxMinCandles}`,
    })
  }

  return { valid: issues.every((i) => i.level !== 'error'), issues, minCandles }
}
